import { useEffect, useState } from "react";
import { Button, Grid2, IconButton, Typography } from "@mui/material";
import { Close } from "@mui/icons-material";
import {
  Eventcalendar,
  locale,
  MbscCalendarEvent,
  MbscCalendarEventData,
  MbscEventClickEvent,
  MbscEventUpdateEvent
} from "@mobiscroll/react";
import { useTranslation } from "react-i18next";
import { DateTime } from "luxon";
import { useDispatch, useSelector } from "../../app/helpers";
import { RootState } from "../../app/rootReducer";
import { setInputs } from "../../features/Booking-Form/slices/booking.slice";
import { NotSerializedBookingInputs } from "../../features/Booking-Form/typings/booking-inputs";
import { EVENT_CALENDAR_GER } from "../Schedule/schedule-calendar-localization";
import { toLuxonDateTime, validateEventUpdate } from "./functions/validateEventUpdate.function";
import { updateMbscSchedulesTime } from "./functions/updateMbscScheduleTime.function";
import { PlaceBookingDetailDialog } from "../FacilityManager/Components/Views/SelectFloorPlanView/PlaceSelectionPrompt/PlaceBookingDetailDialog";
import { OccupiedStatus } from "../FacilityManager/Domain/Types/Status";
import { getCombinedSchedule } from "./functions/getCombinedSchedule.function";
import { clampEditTime } from "./functions/clampEditTime.function";
import { BookingTimeEditFieldDialog } from "./FormEditTimeDialog/booking-time-edit-field-dialog.component";

export type BookingTimeTemp = {
  bookingStart: string | undefined;
  bookingEnd: string | undefined;
};

type P = {
  inputs: NotSerializedBookingInputs;
  occupiedBookingData: OccupiedStatus[];
  setIsDateOpen: (o: boolean) => void;
};

export const FormEditTimeDialog: React.FC<P> = props => {
  const { inputs, occupiedBookingData, setIsDateOpen } = props;
  const {
    settings: { language }
  } = useSelector((state: RootState) => state.login);

  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();

  const [tempTime, setTempTime] = useState<BookingTimeTemp>({
    bookingStart: undefined,
    bookingEnd: undefined
  });

  const [inputSchedule, setInputSchedule] = useState<MbscCalendarEvent[]>([]);
  const [occupiedEvents, setOccupiedEvents] = useState<MbscCalendarEvent[]>([]);
  const [visibleDays, setVisibleDays] = useState<{ startDay: number; endDay: number }>({
    startDay: 1,
    endDay: 5
  });
  const [isOccupiedDialogOpen, setIsOccupiedDialogOpen] = useState(false);
  const [isTimeFieldDialogOpen, setIsTimeFieldDialogOpen] = useState(false);

  const getCalendarEventContent = (event: MbscCalendarEventData) => {
    return (
      <Grid2
        container
        data-testid="selected-schedule-item-container"
        sx={{ overflowWrap: "anywhere", flexDirection: "column" }}
      >
        <Grid2>{event.title}</Grid2>
        <Grid2>
          {event.start}
          {" - "}
          {event.end}
        </Grid2>
      </Grid2>
    );
  };

  function handleTimeChange(newBookingStart: string, newBookingEnd: string) {
    // update temp time state and booking inputs
    setTempTime({ bookingStart: newBookingStart, bookingEnd: newBookingEnd });
    // update input schedules with new time. should stay in the each day
    setInputSchedule(updateMbscSchedulesTime(inputSchedule, newBookingStart, newBookingEnd));
  }

  function onEventUpdate(event: MbscEventUpdateEvent) {
    const { id: newEvId, start: newEvStart, end: newEvEnd } = event.event;
    const oldEvStart = event.oldEvent?.start;
    const oldEvEnd = event.oldEvent?.end;

    if (!newEvId || !newEvStart || !newEvEnd || !oldEvStart || !oldEvEnd) return;

    // validate whether the day is changed or try updated but same time
    if (!validateEventUpdate({ newEvStart, newEvEnd, oldEvStart, oldEvEnd })) return;

    // clamp the edit time, prohibit start goes yesterday or end goes tomorrow
    const { clampedStart, clampedEnd } = clampEditTime({
      start: toLuxonDateTime(newEvStart),
      end: toLuxonDateTime(newEvEnd)
    });

    const newBookingStart = clampedStart.toFormat("HH:mm");
    const newBookingEnd = clampedEnd.toFormat("HH:mm");

    handleTimeChange(newBookingStart, newBookingEnd);
  }

  function onEventClick(args: MbscEventClickEvent) {
    // open occupied detail dialog
    if (inputSchedule.some(schedule => schedule.id === args.event.id)) {
      // open editing time picker dialog
      setIsTimeFieldDialogOpen(true);
    } else {
      setIsOccupiedDialogOpen(true);
    }
  }

  useEffect(() => {
    // set initial temp time state from the stored booking inputs
    setTempTime({
      bookingStart: inputs.bookingStart ?? "08:00",
      bookingEnd: inputs.bookingEnd ?? "17:00"
    });
  }, [inputs]);

  useEffect(() => {
    // set input schedules for the calendar from the booking inputs information
    const { occupiedEvents, selectedEvent } = getCombinedSchedule(
      occupiedBookingData,
      inputs.bookingStart ?? "08:00",
      inputs.bookingEnd ?? "17:00",
      inputs.timezone
    );

    setOccupiedEvents(occupiedEvents);
    setInputSchedule(selectedEvent);
    setVisibleDays({
      startDay: DateTime.now().plus({ days: 1 }).startOf("day").weekday,
      endDay: DateTime.now().plus({ days: 1 }).startOf("day").weekday
    });
  }, [inputs, occupiedBookingData]);

  return (
    <Grid2 container data-testid="edit-schedule-time-modal-container" flexDirection={"column"}>
      <Grid2 container data-testid="edit-schedule-time-modal-header">
        <Grid2 size={{ xs: 2 }} />
        <Grid2 size={{ xs: 8 }} sx={{ placeContent: "center" }}>
          <Typography align={"center"} variant={"h5"}>
            {t("Time")}
          </Typography>
        </Grid2>
        <Grid2 size={{ xs: 2 }} sx={{ textAlign: "right", placeSelf: "center" }}>
          <IconButton
            data-testid={"edit-schedule-time-modal-close"}
            onClick={() => setIsDateOpen(false)}
          >
            <Close />
          </IconButton>
        </Grid2>
      </Grid2>

      <Grid2 container sx={{ flexDirection: "column", mt: 2 }}>
        <Grid2 data-testid="occuiped-schedule-container" sx={{ pb: 3 }}>
          <Eventcalendar
            height={"65vh"}
            locale={locale[i18n.language]}
            {...(language === "de" ? EVENT_CALENDAR_GER : [])}
            firstDay={1}
            timeFormat="H:mm"
            dragToResize={true}
            dragToMove={true}
            view={{
              schedule: {
                type: "week",
                currentTimeIndicator: true,
                startDay: visibleDays.startDay,
                endDay: visibleDays.endDay,
                allDay: false
              }
            }}
            data={[...occupiedEvents, ...inputSchedule]}
            onEventClick={onEventClick}
            onEventUpdate={onEventUpdate}
            renderScheduleEventContent={getCalendarEventContent}
            renderLabelContent={getCalendarEventContent}
            renderEventContent={getCalendarEventContent}
            /** start of empty header */
            showControls={false}
            renderDay={() => {}}
            allDayText=""
            /** end of empty header */
          />
        </Grid2>

        <Grid2 sx={{ alignSelf: "center" }}>
          <Button
            data-testid={"edit-schedule-time-modal-confirm"}
            sx={{ height: "fit-content" }}
            onClick={() => {
              dispatch(
                setInputs({
                  bookingStart: tempTime.bookingStart,
                  bookingEnd: tempTime.bookingEnd
                })
              );
              setIsDateOpen(false);
            }}
          >
            {t("Confirm")}
          </Button>
        </Grid2>
      </Grid2>

      <PlaceBookingDetailDialog
        isDialogOpen={isOccupiedDialogOpen}
        setIsDialogOpen={setIsOccupiedDialogOpen}
        timezone={inputs.timezone}
        occupiedStatus={occupiedBookingData}
      />

      {tempTime.bookingStart && tempTime.bookingEnd && isTimeFieldDialogOpen && (
        <BookingTimeEditFieldDialog
          open={isTimeFieldDialogOpen}
          setOpen={setIsTimeFieldDialogOpen}
          tempTime={tempTime}
          handleChange={tempTime => {
            /* istanbul ignore next */
            if (!tempTime.bookingStart || !tempTime.bookingEnd) return;
            handleTimeChange(tempTime.bookingStart, tempTime.bookingEnd);
          }}
          timezone={inputs.timezone}
        />
      )}
    </Grid2>
  );
};
